import { useCallback, useEffect, useState } from 'react';
import type { Conn } from '../conn';
import { getUsage, putBudget, generateApiKey, type UsageResult } from '../api';

export default function UsagePage({ conn }: { conn: Conn }) {
  const [data, setData] = useState<UsageResult | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [budgetFor, setBudgetFor] = useState<string | null>(null);
  const [cap, setCap] = useState('25');
  const [minted, setMinted] = useState<string>('');

  const load = useCallback(async () => {
    if (!conn.apiKey) { setErr('Enter an API key (top bar) to load usage.'); return; }
    setErr(''); setBusy(true);
    try { setData(await getUsage(conn.baseUrl, conn.apiKey)); }
    catch (e) { setErr(String(e)); }
    finally { setBusy(false); }
  }, [conn.baseUrl, conn.apiKey]);

  useEffect(() => { load(); }, [load]);

  async function saveBudget(key: string) {
    setBusy(true); setErr('');
    try {
      await putBudget(conn.baseUrl, conn.apiKey, key, {
        cap_usd: parseFloat(cap) || 0, period: '30d', alert_pct: 80, hard_stop: true,
      });
      setBudgetFor(null); await load();
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }

  async function mint() {
    setBusy(true); setErr('');
    try {
      const r = await generateApiKey(conn.baseUrl, conn.apiKey, 'all', 'console-minted');
      setMinted(r.api_key || ''); await load();
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }

  return (
    <div className="page">
      <header className="page-head">
        <h1>Usage, Pricing &amp; Budget</h1>
        <div className="row">
          <button onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
          <button onClick={mint} disabled={busy || !conn.apiKey}>+ Mint key</button>
        </div>
      </header>
      {err && <p className="err">{err}</p>}
      {minted && <p className="ok">Minted key (store it now): <code>{minted}</code></p>}

      {data && (
        <>
          <div className="cards">
            <div className="card"><div className="card-k">Calls</div><div className="card-v">{data.totals.calls}</div></div>
            <div className="card"><div className="card-k">Tokens (in/out)</div><div className="card-v">{data.totals.input_tokens}/{data.totals.output_tokens}</div></div>
            <div className="card"><div className="card-k">Spend (est.)</div><div className="card-v">${data.totals.cost_usd.toFixed(4)}</div></div>
            <div className="card"><div className="card-k">Scope</div><div className="card-v">{data.admin ? 'all keys' : data.namespace}</div></div>
          </div>

          <table className="grid">
            <thead><tr><th>key</th><th>calls</th><th>tokens</th><th>cost</th><th>budget</th><th></th></tr></thead>
            <tbody>
              {data.keys.length === 0 && <tr><td colSpan={6} className="muted">No usage yet — run an agent on the Agents page.</td></tr>}
              {data.keys.map((k) => (
                <tr key={k.api_key}>
                  <td><code>{k.api_key.slice(0, 14)}…</code></td>
                  <td>{k.calls}</td>
                  <td>{k.input_tokens}/{k.output_tokens}</td>
                  <td>${k.cost_usd.toFixed(4)}</td>
                  <td>
                    {k.budget?.cap_usd != null
                      ? <span className={k.budget.blocked ? 'bad' : k.budget.alerting ? 'warn' : 'ok'}>
                          ${k.budget.spend_usd.toFixed(4)}/${k.budget.cap_usd} ({k.budget.pct_used}%)
                        </span>
                      : <span className="muted">none</span>}
                  </td>
                  <td><button className="link" onClick={() => { setBudgetFor(k.api_key); setCap(String(k.budget?.cap_usd ?? 25)); }}>set budget</button></td>
                </tr>
              ))}
            </tbody>
          </table>

          {budgetFor && (
            <div className="inline-form">
              <span>Cap (USD, 30d, hard-stop) for <code>{budgetFor.slice(0, 14)}…</code></span>
              <input value={cap} onChange={(e) => setCap(e.target.value)} style={{ width: 90 }} />
              <button onClick={() => saveBudget(budgetFor)} disabled={busy}>Save</button>
              <button className="link" onClick={() => setBudgetFor(null)}>Cancel</button>
            </div>
          )}
          <p className="muted small">Costs are estimated from token counts × a public-rate price book; exact spend will come from the tracing/LiteLLM layer.</p>
        </>
      )}
    </div>
  );
}
